import { Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useState } from 'react';
import {
  Button,
  Flex,
  MenuLinkButton,
  ServerErrorCallout,
  Text,
  TextField,
} from '@/components/common';
import { AccountSubpageShell } from '@/components/layout';
import { useCurrentUser } from '@/contexts';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services';
import { DeleteAccountComponent } from './DeleteAccountComponent';

export function MyAccountSettingsPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentUser: profile } = useCurrentUser();
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const userId = profile?.uid ?? user?.uid;

  const handleCopyUserId = async () => {
    if (!userId) return;

    try {
      await navigator.clipboard.writeText(userId);
      toast.success(t('account.copySuccess'));
    } catch (err) {
      console.error('Failed to copy user ID:', err);
      toast.error(t('account.copyFailed'));
    }
  };

  const handleResetPassword = async () => {
    const email = user?.email ?? profile?.email;
    if (!email) return;

    setResetError(null);
    setResetLoading(true);
    try {
      await authService.forgotPassword(email);
      toast.success(t('account.resetSent'));
    } catch (err) {
      console.error(err);
      setResetError(err instanceof Error ? err.message : t('account.resetFailed'));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <AccountSubpageShell
      onBack={() => navigate('/account/settings')}
      title={t('account.title')}
      subtitle={t('account.subtitle')}
    >
      <Flex direction="column" gap="3">
        {resetError && <ServerErrorCallout message={resetError} />}

        {userId && (
          <Flex direction="column" gap="1" pb="2">
            <Text size="1" color="gray" as="div">
              {t('account.userIdLabel')}
            </Text>
            <Text as="p" size="1" color="gray">
              {t('account.userIdHint')}
            </Text>
            <Flex align="center" gap="2" mt="1">
              <TextField.Root disabled value={userId} className="grow font-mono!" />
              <Button
                type="button"
                variant="outline"
                color="gray"
                highContrast
                radius="full"
                size="icon"
                icon={<Copy />}
                onClick={handleCopyUserId}
                aria-label={t('account.copyAria')}
              />
            </Flex>
          </Flex>
        )}

        <MenuLinkButton
          title={t('account.resetPassword.title')}
          subtitle={t('account.resetPassword.subtitle')}
          onClick={() => {
            if (!resetLoading) void handleResetPassword();
          }}
        />
        <DeleteAccountComponent />
      </Flex>
    </AccountSubpageShell>
  );
}
